import { useEffect, useRef, useState } from "react";
import { Search, Target, Rocket, TrendingUp } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface ProcessStep {
  icon: React.ReactNode;
  title: string;
  description: string;
}


const ProcessSection = () => {
  const { t } = useLanguage();
  const [visibleSteps, setVisibleSteps] = useState<boolean[]>([false, false, false, false]);
  const sectionRef = useRef<HTMLElement>(null);

  const steps: ProcessStep[] = [
    {
      icon: <Search className="w-8 h-8" />,
      title: t("process.discovery"),
      description: t("process.discoveryDesc"),
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: t("process.strategy"),
      description: t("process.strategyDesc"),
    },
    {
      icon: <Rocket className="w-8 h-8" />,
      title: t("process.execution"),
      description: t("process.executionDesc"),
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: t("process.growth"),
      description: t("process.growthDesc"),
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          steps.forEach((_, index) => {
            setTimeout(() => {
              setVisibleSteps((prev) => {
                const newState = [...prev];
                newState[index] = true;
                return newState;
              });
            }, index * 200);
          });
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="process" ref={sectionRef} className="pt-20 md:pt-56 pb-16 md:pb-24 px-4 md:px-6 relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-30" />
      
      <div className="container mx-auto max-w-6xl relative z-10">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 md:mb-8 text-center text-glow-hover uppercase">
          {t("process.title")} <span className="text-holographic">{t("process.process")}</span>
        </h2>
        <p className="text-lg md:text-xl text-center text-muted-foreground mb-8 md:mb-12">
          {t("process.subtitle")}
        </p>

        <div className="relative">
          {/* Connection line - vertical on mobile, centered on desktop */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[hsl(var(--holographic-cyan))] to-[hsl(var(--holographic-pink))]" />

          <div className="space-y-8 md:space-y-16">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`flex md:grid md:grid-cols-[1fr_auto_1fr] items-start md:items-center gap-4 md:gap-8 ${
                  visibleSteps[index] ? "animate-slide-in" : "opacity-0"
                }`}
              >
                {/* Mobile layout - icon on left, content on right */}
                <div className="md:hidden flex-shrink-0 relative z-10 rounded-full p-4 border-2 border-[hsl(var(--holographic-cyan))] bg-black shadow-2xl">
                  <div className="text-[hsl(var(--holographic-cyan))]">
                    {step.icon}
                  </div>
                </div>

                <div className="md:hidden flex-1">
                  <div className="glass-card rounded-lg p-4 glow-on-hover">
                    <h3 className="text-xl font-bold mb-2 text-holographic">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </div>
                </div>

                {/* Desktop layout - Left side - shows content for even indexes */}
                <div className="hidden md:block text-center md:text-right">
                  {index % 2 === 0 && (
                    <div className="glass-card rounded-lg p-6 glow-on-hover hover-slide-up">
                      <h3 className="text-2xl font-bold mb-2 text-holographic hover-scale">{step.title}</h3>
                      <p className="text-muted-foreground">{step.description}</p>
                    </div>
                  )}
                </div>

                {/* Desktop - Center - icon */}
                <div className="hidden md:block relative z-10 rounded-full p-6 border-2 border-[hsl(var(--holographic-cyan))] bg-black shadow-2xl">
                  <div className="text-[hsl(var(--holographic-cyan))]">
                    {step.icon}
                  </div>
                </div>

                {/* Desktop - Right side - shows content for odd indexes */}
                <div className="hidden md:block text-center md:text-left">
                  {index % 2 !== 0 && (
                    <div className="glass-card rounded-lg p-6 glow-on-hover hover-slide-up">
                      <h3 className="text-2xl font-bold mb-2 text-holographic hover-scale">{step.title}</h3>
                      <p className="text-muted-foreground">{step.description}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
